import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { createElement } from "react";
import { flushSync } from "react-dom";
import { Suggestion } from "@tiptap/suggestion";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import type { PickerListItem } from "@/features/sparxsEditor/components/molecules/pickerList/pickerList";
import {
  mountSuggestionRoot,
  unmountSuggestionRoot,
  type SuggestionReactRoot,
} from "@/features/sparxsEditor/components/organisms/richTextEditor/lib/suggestionReactRoot";
import { DirectiveBuilderFloating } from "../../builder/interaction/floating";
import {
  filterDirectiveItems,
  getDirectiveBuilderStepProps,
  getItemsForKeyDown,
} from "../logic/stepProps";
import type { DirectiveBuilderState } from "../logic/printTable";
import {
  finalizeDirectiveTokenRawAtPos,
  updateDirectiveTokenRawAtPos,
} from "../logic/updateToken";
import {
  clearFloatingBuilderChoiceStorage,
  syncFloatingBuilderChoiceStorage,
} from "../storage/floatingStorage";
import { handleDirectiveBuilderFloatingKeyDown } from "../../builder/interaction/keyboardRouter";
import { DIRECTIVE_BUILDER_CHOICE_FIELD_ID } from "../storage/session";
import { getDirectiveSuggestionFloatingPanelClientRect } from "../logic/panelRect";
import { RTE_NO_VARIABLES } from "@/constants/uiCopy/richTextEditor";
import "@/types/tiptapDirectiveSuggestionStorage";

export interface DirectiveSuggestionExtensionOptions {
  variables: Variables[];
}

const initialBuilderState = (): DirectiveBuilderState => ({
  step: "directive",
  directiveKey: "",
  variableName: "",
  choices: [],
  choiceIndex: 0,
  selectedIndex: 0,
});

export const DirectiveSuggestionExtension =
  Extension.create<DirectiveSuggestionExtensionOptions>({
    name: "directiveSuggestion",

    addOptions() {
      return {
        variables: [],
      };
    },

    addStorage() {
      return {
        floatingBuilderChoiceAtPos: null as number | null,
      };
    },

    addProseMirrorPlugins() {
      const { variables } = this.options;
      const pluginKey = new PluginKey("directiveSuggestion");
      const { editor } = this;

      const stateRef: {
        container: HTMLDivElement | null;
        root: SuggestionReactRoot | null;
        builder: DirectiveBuilderState;
        listFilterText: string;
        directiveTokenFrom: number | null;
        currentProps: {
          items: PickerListItem[];
          command: (item: PickerListItem) => void;
          clientRect: (() => DOMRect | null) | null;
        } | null;
        currentOnSelectItem: ((item: PickerListItem) => void) | null;
        update: () => void;
        syncDocumentDirectiveRaw: (raw: string) => void;
        blurDomBeforeFloatingChoiceFocus?: () => void;
        floatingUiContainer: HTMLElement | null;
        floatingChoiceFieldEl: HTMLElement | null;
      } = {
        container: null,
        root: null,
        builder: initialBuilderState(),
        listFilterText: "",
        directiveTokenFrom: null,
        currentProps: null,
        currentOnSelectItem: null,
        update: () => undefined,
        syncDocumentDirectiveRaw: () => undefined,
        floatingUiContainer: null,
        floatingChoiceFieldEl: null,
      };

      const handleListFilterChange = (v: string) => {
        stateRef.listFilterText = v;
        stateRef.builder = {
          ...stateRef.builder,
          selectedIndex: 0,
        };
        stateRef.update();
      };

      const handleListHighlightChange = (index: number) => {
        stateRef.builder = {
          ...stateRef.builder,
          selectedIndex: index,
        };
        stateRef.update();
      };

      stateRef.blurDomBeforeFloatingChoiceFocus = () => {
        editor.view.dom.blur();
      };

      stateRef.syncDocumentDirectiveRaw = (raw: string) => {
        const pos = stateRef.directiveTokenFrom;
        if (typeof pos !== "number" || pos < 0) return;
        updateDirectiveTokenRawAtPos(editor, pos, raw, { focus: false });
      };

      const cleanup = () => {
        clearFloatingBuilderChoiceStorage(editor);
        unmountSuggestionRoot(stateRef.container, stateRef.root);
        stateRef.container = null;
        stateRef.root = null;
        stateRef.currentProps = null;
        stateRef.currentOnSelectItem = null;
        stateRef.update = () => undefined;
        stateRef.directiveTokenFrom = null;
        stateRef.blurDomBeforeFloatingChoiceFocus = undefined;
        stateRef.floatingUiContainer = null;
        stateRef.floatingChoiceFieldEl = null;
        stateRef.listFilterText = "";
      };

      const insertAndClose = (raw: string) => {
        const pos = stateRef.directiveTokenFrom;
        if (typeof pos === "number" && pos >= 0) {
          const ok = finalizeDirectiveTokenRawAtPos(editor, pos, raw);
          if (ok) {
            cleanup();
            return;
          }
        }
        stateRef.currentProps?.command({ raw, label: "" });
        cleanup();
      };

      const directiveBuilderListKeyPluginKey = new PluginKey(
        "directiveBuilderListKey",
      );

      return [
        new Plugin({
          key: directiveBuilderListKeyPluginKey,
          props: {
            handleKeyDown(view, event) {
              return handleDirectiveBuilderFloatingKeyDown(
                view,
                event,
                stateRef,
                variables,
                DIRECTIVE_BUILDER_CHOICE_FIELD_ID,
              );
            },
          },
        }),
        Suggestion<PickerListItem, PickerListItem>({
          pluginKey,
          editor: this.editor,
          char: "@",
          allowedPrefixes: null,
          allow: ({ editor: ed, range }) => {
            const { from } = ed.state.selection;
            if (from < range.from || from > range.to) return false;
            const text = ed.state.doc.textBetween(range.from, range.to);
            const query = text.slice(1);
            return /^[\w\-()]*$/.test(query);
          },
          items: () => filterDirectiveItems(variables, stateRef.listFilterText),
          command: ({ editor: ed, range, props }) => {
            stateRef.directiveTokenFrom = range.from;
            ed.chain()
              .focus()
              .deleteRange(range)
              .insertContent({
                type: "variableToken",
                attrs: { raw: props.raw },
              })
              .run();
          },
          render: () => ({
            onStart: (props) => {
              const { editor: ed, range } = props;
              const docText = ed.state.doc.textBetween(range.from, range.to);
              const seedQuery = docText.slice(1);
              stateRef.listFilterText = seedQuery;
              if (range.to > range.from + 1) {
                ed.chain()
                  .focus()
                  .deleteRange({ from: range.from + 1, to: range.to })
                  .run();
              }
              stateRef.directiveTokenFrom = null;
              stateRef.currentProps = {
                items: filterDirectiveItems(variables, stateRef.listFilterText),
                command: props.command,
                clientRect: props.clientRect ?? null,
              };
              stateRef.builder = initialBuilderState();
              const mounted = mountSuggestionRoot(ed.view.dom.ownerDocument);
              stateRef.container = mounted.container;
              stateRef.root = mounted.root;
              stateRef.update = () => {
                const { root } = stateRef;
                if (root == null || stateRef.currentProps == null) return;
                syncFloatingBuilderChoiceStorage(editor, stateRef);
                const props = stateRef.currentProps;
                const b = stateRef.builder;
                const stepProps = getDirectiveBuilderStepProps(
                  editor,
                  stateRef,
                  variables,
                  props,
                  insertAndClose,
                );
                stateRef.currentOnSelectItem = stepProps.onSelectItem;
                const floatingClientRect =
                  getDirectiveSuggestionFloatingPanelClientRect({
                    editor,
                    suggestionClientRect: props.clientRect ?? null,
                    directiveTokenFrom: stateRef.directiveTokenFrom,
                  });
                // Synchronous commit keeps TipTap suggestion + floating UI in the same frame as the ProseMirror update
                // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- required here for coalesced tiptap update + render
                flushSync(() => {
                  root.render(
                    createElement(DirectiveBuilderFloating, {
                      clientRect: floatingClientRect,
                      step: b.step,
                      items: stepProps.items,
                      selectedIndex: b.selectedIndex,
                      onSelectItem: stepProps.onSelectItem,
                      choiceSlot: stepProps.choiceSlot,
                      choiceInitialValue: stepProps.choiceInitialValue,
                      onChoiceSubmit: stepProps.onChoiceSubmit,
                      stepTitle: stepProps.stepTitle,
                      emptyMessage: RTE_NO_VARIABLES,
                      choiceStepKey: b.choiceIndex,
                      listFilterText: stateRef.listFilterText,
                      onListFilterChange: handleListFilterChange,
                      onListHighlightChange: handleListHighlightChange,
                      onFloatingPanelRef: (el: HTMLDivElement | null) => {
                        stateRef.floatingUiContainer = el;
                      },
                      onFloatingChoiceFieldRef: (el: HTMLElement | null) => {
                        stateRef.floatingChoiceFieldEl = el;
                      },
                    }),
                  );
                });
              };
              stateRef.update();
            },
            onUpdate: (props) => {
              if (stateRef.currentProps == null) return;
              const b = stateRef.builder;
              const listItems =
                b.step === "directive" || b.step === "variable"
                  ? getItemsForKeyDown(
                      b,
                      variables,
                      stateRef.currentProps,
                      stateRef.listFilterText,
                    )
                  : props.items;
              stateRef.currentProps = {
                items: listItems,
                command: props.command,
                clientRect: props.clientRect ?? null,
              };
              if (b.step === "directive" || b.step === "variable") {
                stateRef.builder = {
                  ...b,
                  selectedIndex: Math.min(
                    b.selectedIndex,
                    Math.max(0, listItems.length - 1),
                  ),
                };
              }
              stateRef.update();
            },

            onExit: () => {
              if (stateRef.directiveTokenFrom == null) {
                cleanup();
              }
            },
            onKeyDown: ({ event }) =>
              handleDirectiveBuilderFloatingKeyDown(
                null,
                event,
                stateRef,
                variables,
                DIRECTIVE_BUILDER_CHOICE_FIELD_ID,
              ),
          }),
        }),
      ];
    },
  });
